import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getAnonId } from "@/lib/api";
import { getSessionToken, loadSessionToken, setSessionToken } from "@/lib/session";

const UserCtx = createContext(null);

const EMPTY_SUB_INFO = {
  subscription: {
    active: false,
    status: "none",
    plan: null,
    trial: false,
    expires_at: null,
    cancelled: false,
    days_remaining: 0,
    trial_used: false,
  },
  premium_track_ids: [],
  free_track_ids: [],
  excluded_track_ids: [],
  plans: [],
  trial_days: 7,
  payments_mode: "mock",
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(null); // { user_id, email, name, picture, is_anonymous }
  const [progress, setProgress] = useState({
    user_id: getAnonId(),
    xp: 0,
    streak: 0,
    completed_lessons: [],
    badges: [],
  });
  const [subInfo, setSubInfo] = useState(EMPTY_SUB_INFO);
  const [bootLoading, setBootLoading] = useState(true);
  // Monotonic counter bumped on every refresh(). Downstream pages depend on
  // this (not on completed_lessons.length) to decide when to refetch server-
  // computed stats. Length-based detection breaks on same-length changes
  // (e.g. anon→authed merge where the merged set happens to equal the
  // previous size, or completing then uncompleting via admin reset).
  const [progressVersion, setProgressVersion] = useState(0);

  const refreshSubscription = useCallback(async () => {
    try {
      const info = await api.mySubscription();
      setSubInfo(info);
      return info;
    } catch (e) {
      console.warn("subscription refresh failed", e);
      return null;
    }
  }, []);

  // `knownMe`: caller already verified /auth/me (native login) → skip the duplicate call.
  const refresh = useCallback(async (knownMe) => {
    try {
      // Skip /auth/me if we just landed from OAuth (AuthCallback handles it)
      const skipMe = window.location.hash?.includes("session_id=");
      if (knownMe?.user_id) {
        setUser(knownMe);
      } else if (!skipMe) {
        try {
          const me = await api.me();
          if (me?.is_anonymous && getSessionToken()) await setSessionToken(null);
          setUser(me);
        } catch (e) {
          // Stored native token expired/revoked → drop it and fall back to guest.
          if (getSessionToken() && e?.response?.status === 401) await setSessionToken(null);
          // Anonymous mode — ensure an anon user exists via header
          setUser({
            user_id: getAnonId(),
            email: null,
            name: null,
            picture: null,
            is_anonymous: true,
          });
        }
      }
      const [p] = await Promise.all([api.myProgress(), refreshSubscription()]);
      setProgress(p);
      setProgressVersion((v) => v + 1);
    } catch (e) {
      console.error("user refresh failed", e);
    } finally {
      setBootLoading(false);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    loadSessionToken().catch(() => null).finally(() => refresh());
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch (_) {}
    await setSessionToken(null);
    // Reset anon id so user starts fresh after logout
    localStorage.removeItem("balagha_uid");
    setUser(null);
    setSubInfo(EMPTY_SUB_INFO);
    window.location.href = "/";
  }, []);

  const userId = user?.user_id || getAnonId();
  const isAuthed = !!user && !user.is_anonymous;
  const premiumActive = !!subInfo?.subscription?.active;

  return (
    <UserCtx.Provider
      value={{
        user,
        userId,
        isAuthed,
        progress,
        setProgress,
        progressVersion,
        refresh,
        signOut,
        bootLoading,
        subInfo,
        premiumActive,
        refreshSubscription,
      }}
    >
      {children}
    </UserCtx.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserCtx);
  if (!ctx) throw new Error("useUser must be inside UserProvider");
  return ctx;
}
